import { useEffect, useRef, useState, useMemo, Suspense } from "react";
import { CellProps, ColumnRegular, RevoGrid } from "@revolist/react-datagrid";
import "./DemoReport.css";
import Button from "antd/lib/button";
import Select from "antd/lib/select";
import DatashaperService from "../../../../services/datashaper/DatashaperService";
import {
  CloseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ExportOutlined,
  LoadingOutlined,
  ReloadOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { Divider, Drawer, Form, Space, Switch, Tabs, message } from "antd";
import { Colors } from "chart.js";
import Updates from "../demo/components/Updates";
import {Update} from "../../../../models/reports/Update";
import {useSelector} from "react-redux";

export enum DataShaperViewType {
  Enrichment = 1,
  IssueList = 2,
  CustomTable = 3,
  Clustering = 4,
  MappedVsEnrichment = 5,
}

export interface Table {
  id: number;
  table: string;
  entity: string;
  viewTypeId: DataShaperViewType;
  defaultPermission: DefaultPermissions;
  allowRowCreateDelete: boolean;
}

export enum DefaultPermissions {
  Edit = 1,
  View = 2,
}

export interface DataTypeItem {
  id: number;
  columnName: string;
  dataType: string;
  characterMaximumLength: number;
  numericPrecision: number;
  numericScale: number;
  typeId?: number;
  referenceListValue?: string;
  referenceListValueCanUserAdd?: boolean;
}

const DemoReport = () => {
  const { profile } = useSelector((state: any) => ({
    profile: state.profile,
  }));
  
  const [columns, setColumns] = useState([]);
  const [source, setSource] = useState([]);
  const [changedCells, setChangedCells] = useState([]);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const gridRef = useRef(null);
  const [showNoEnititiesFound, setShowNoEnititiesFound] = useState(false);
  const [entitiesLoading, setEntitiesLoading] = useState(true);
  const [entities, setEntities] = useState<string[]>([]);
  const [tables, setTables] = useState<Table[]>([]);
  const [currentTables, setCurrentTables] = useState<Table[]>([]);
  const [resetFieldsEnabled, setResetFieldsEnabled] = useState(true);
  const [groupCodes, setGroupCodes] = useState<string[]>([]);
  const [environments, setEnvironments] = useState<string[]>([]);
  const [editableFields, setEditableFields] = useState<string[]>([]);
  const [dataTypes, setDataTypes] = useState<DataTypeItem[]>([]);
  const [deleteDisabled, setDeleteDisabled] = useState(false);
  const [selectedGroupCode, setSelectedGroupCode] = useState<string>();
  const [selectedEnvironment, setSelectedEnvironment] = useState<string>();
  const [selectedTable, setSelectedTable] = useState<string | number>();
  const [selectedEntity, setSelectedEntity] = useState<string>();
  const [fieldsWithUpdates, setFieldsWithUpdates] = useState([]);
  const [issues, setIssues] = useState([]);
  const [counter, setCounter] = useState(1);
  const [deleteRows, setDeleteRows] = useState([]);
  const [canBeCreatedAndUpdated, setCanBeCreatedAndUpdated] = useState(false);
  const [open, setOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false); // FIX #2: Separate loading state for publish
  const [changedVersion, setChangedVersion] = useState(0);
  const [isPasting, setIsPasting] = useState(false);
  const [baselineByCode, setBaselineByCode] = useState<Map<string, any>>(new Map());
  const [updates, setUpdates] = useState<Array<Update>>([]);
  const [selectedField, setSelectedField] = useState<any>();
  const [selectedCode, setSelectedCode] = useState<any>();

  const columnsRef = useRef(columns);
  useEffect(() => {
    columnsRef.current = columns;
  }, [columns]);

  const lastFocusedRef = useRef<{ rowIndex: number; columnIndex: number } | null>(null);

  const sourceRef = useRef(source);
  useEffect(() => {
    sourceRef.current = source;
  }, [source]);

  /** ✅ Optimized lookup maps */
  const changedSet = useMemo(
      () => new Set(changedCells.map((c) => c.key)),
      [changedCells]
  );
  const fieldsUpdateMap = useMemo(
      () => new Map(fieldsWithUpdates.map((f) => [`${f.code}_${f.field}`, true])),
      [fieldsWithUpdates]
  );
  const issuesMandatoryMap = useMemo(
      () =>
          new Map(
              issues
                  .filter((i) => !i.issueMandatory)
                  .map((i) => [`${i.issueRow}_${i.issueColumn}`, true])
          ),
      [issues]
  );
  const issuesMap = useMemo(
      () => new Map(issues.map((i) => [`${i.issueRow}_${i.issueColumn}`, true])),
      [issues]
  );

  const measureTextWidth = (text, font = "12px Arial") => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    ctx.font = font;
    return ctx.measureText(text).width;
  };

  const cellClassMap = useMemo(() => {
    console.log("✅ Recomputing cellClassMap (baseline-aware)");
    const map = new Map<string, string>();

    source.forEach((row, rowIndex) => {
      const rowCode = String(row?.Code ?? "");
      const baseline = baselineByCode.get(rowCode);

      columns.forEach((col) => {
        const key = `${rowIndex}_${col.prop}`;
        const issueKey = `${rowCode}_${col.prop}`;

        // FIX #1: Check changed cells first (has priority over updates)
        // This ensures that cells that were green (updates) turn yellow when edited
        let isChanged = false;
        if (baseline) {
          const oldVal = (baseline as any)[col.prop];
          const newVal = (row as any)[col.prop];
          isChanged = newVal !== oldVal;
        } else {
          // New/virtual rows have no baseline; honor explicit changedSet marks
          isChanged = changedSet.has(key);
        }

        if (isChanged) {
          map.set(key, "cell-changed");
          return; // Changed cells take priority
        }

        // Now check for updates (green) - only if not changed
        if (fieldsUpdateMap.has(issueKey)) {
          map.set(key, "cell-update");
          return;
        }

        // Issues last
        if (issuesMandatoryMap.has(issueKey)) {
          map.set(key, "cell-warning");
          return;
        }
        if (issuesMap.has(issueKey)) {
          map.set(key, "cell-error");
          return;
        }
      });
    });

    return map;
  }, [
    source,
    columns,
    baselineByCode,
    fieldsUpdateMap,
    changedSet,
    issuesMandatoryMap,
    issuesMap,
    changedVersion,
  ]);

  const styledColumns = useMemo(() => {
    return columns.map((col) => ({
      ...col,
      cellProperties: ({ rowIndex }) => {
        const key = `${rowIndex}_${col.prop}`;
        return { class: cellClassMap.get(key) || "" };
      },
    }));
  }, [columns, cellClassMap]);

  /** ✅ Data fetching */
  useEffect(() => {
    (async () => {
      try {
        if (profile?.email) {
          const response = await DatashaperService.getEntities(
              profile?.email,
              selectedGroupCode
          );
          setEntities(response.data);
          setEntitiesLoading(false);
        }
      } catch (error: any) {
        if (error.response.status === 404) {
          setEntitiesLoading(false);
          setShowNoEnititiesFound(true);
        }
      }
    })();
  }, [profile, selectedGroupCode]);

  useEffect(() => {
    (async () => {
      if (profile?.email && selectedEntity) {
        let availableList = tables;
        if (availableList.length === 0) {
          const data = (await DatashaperService.getTables(profile?.email)).data;
          setTables(data);
          availableList = data;
        }
        setCurrentTables(
            availableList.filter((x) => x.entity === selectedEntity)
        );
        setResetFieldsEnabled(true);
      }
    })();
  }, [profile, selectedEntity]);

  useEffect(() => {
    (async () => {
      const dataGroupCodes = (
          await DatashaperService.getGroupCodes(profile?.email, -1)
      ).data;
      setGroupCodes(dataGroupCodes);
      const dataEnvironments = (
          await DatashaperService.getEnvironments(profile?.email, -1)
      ).data;
      setEnvironments(dataEnvironments);
    })();
  }, []);

  /** ✅ Undo / Redo logic */
  useEffect(() => {
    const gridEl = gridRef.current?.shadowRoot?.host || gridRef.current;
    if (!gridEl) return;
    const handleKeyDown = (e) => {
      const isUndo = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z";
      const isRedo = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "y";
      if (isUndo && undoStack.length > 0) {
        e.preventDefault();
        const lastEdit = undoStack.pop();
        setRedoStack((prev) => [...prev, lastEdit]);
        setSource((prev) => {
          const updated = [...prev];
          updated[lastEdit.rowIndex] = {
            ...updated[lastEdit.rowIndex],
            [lastEdit.prop]: lastEdit.oldValue,
          };
          return updated;
        });
      }
      if (isRedo && redoStack.length > 0) {
        e.preventDefault();
        const lastRedo = redoStack.pop();
        setUndoStack((prev) => [...prev, lastRedo]);
        setSource((prev) => {
          const updated = [...prev];
          updated[lastRedo.rowIndex] = {
            ...updated[lastRedo.rowIndex],
            [lastRedo.prop]: lastRedo.newValue,
          };
          return updated;
        });
      }
    };
    gridEl.addEventListener("keydown", handleKeyDown);
    return () => gridEl.removeEventListener("keydown", handleKeyDown);
  }, [undoStack, redoStack]);

  /** ✅ Event Handlers */
  const handleBeforeEdit = (e) => {
    const { model, prop, rowIndex } = e.detail;
    setUndoStack((prev) => [
      ...prev,
      { rowIndex, prop, oldValue: model[prop] },
    ]);
  };

  const handleChange = (e: any) => {
    if (isPasting) return;
    const d = e.detail;
    if (!d) return;

    console.log("handleChange called with:", d);

    // --- Single cell ----------------------------------------------------------
    if (d.model && typeof d.rowIndex === 'number' && d.prop) {
      const { prop, rowIndex, val, model } = d;
      const key = `${rowIndex}_${prop}`;
      const code = model?.Code ?? '';
      setChangedCells(prev => {
        const map = new Map(prev.map(c => [c.key, c]));
        map.set(key, { key, code, column: prop, value: val });
        return [...map.values()];
      });
      // Mirror the edit into React state so baseline diff triggers instantly
      setSource(prev => {
        const updated = [...prev];
        const row = { ...(updated[rowIndex] || {}) };
        (row as any)[prop] = val;
        updated[rowIndex] = row;
        return updated;
      });
      setChangedVersion(v => v + 1);
      return;
    }

    // --- Autofill/drag specific handling --------------------------------------
    // Check if this is an autofill event by looking for the data property with changed values
    if (d.data && d.models && d.newRange) {
      const items: Array<{ key: string; code: string; column: string; value: any }> = [];

      // d.data contains the changed values: { rowIndex: { prop: value } }
      Object.entries(d.data as Record<string, any>).forEach(([rowIndexStr, changedProps]) => {
        const rowIndex = Number(rowIndexStr);
        const rowModel = d.models[rowIndexStr]; // Get the full row model
        const code = rowModel?.Code ?? '';

        // Process each changed property in this row
        Object.entries(changedProps as Record<string, any>).forEach(([prop, value]) => {
          if (prop === 'Code') return; // Skip Code column
          const key = `${rowIndex}_${prop}`;
          items.push({ key, code, column: prop, value });
        });
      });

      if (items.length) {
        setChangedCells(prev => {
          const map = new Map(prev.map(c => [c.key, c]));
          items.forEach(c => map.set(c.key, c));
          return [...map.values()];
        });
        // Apply range edits into React state so baseline diff triggers instantly
        setSource(prev => {
          const updated = [...prev];
          items.forEach(({ key, column, value }) => {
            const [rowIndexStr] = key.split('_');
            const rowIndex = Number(rowIndexStr);
            const row = { ...(updated[rowIndex] || {}) };
            (row as any)[column] = value;
            updated[rowIndex] = row;
          });
          return updated;
        });
        setChangedVersion(v => v + 1);
      }
      return;
    }

    // --- Range / multi-cell (fallback for other cases) --------------------------------------
    if (d.models) {
      // Try to resolve row span
      const startRow =
          (d.newRange && typeof d.newRange.y === 'number' && d.newRange.y) ??
          (d.range && typeof d.range.y === 'number' && d.range.y) ??
          (typeof d.rowIndex === 'number' ? d.rowIndex : 0);

      // Try to resolve col span (optional; if missing, we'll infer from props)
      const hasColRange =
          d.newRange && typeof d.newRange.x === 'number' && typeof d.newRange.x1 === 'number'
              ? true
              : d.range && typeof d.range.x === 'number' && typeof d.range.x1 === 'number'
                  ? true
                  : false;
      const colStart = hasColRange
          ? (d.newRange?.x ?? d.range?.x)
          : undefined;
      const colEnd = hasColRange
          ? (d.newRange?.x1 ?? d.range?.x1)
          : undefined;

      const items: Array<{ key: string; code: string; column: string; value: any }> = [];

      const pushCell = (rowIndex: number, prop: string, newVal: any) => {
        if (!prop || prop === 'Code') return;
        const before = sourceRef.current?.[rowIndex] || {};
        const oldVal = (before as any)[prop];
        if (typeof newVal === 'undefined' || newVal === oldVal) return;

        // If we do have a col range, ensure the prop sits inside of it
        if (hasColRange && Array.isArray(columnsRef.current)) {
          const cidx = columnsRef.current.findIndex(c => c?.prop === prop);
          if (cidx === -1 || cidx < (colStart as number) || cidx > (colEnd as number)) return;
        }

        const code = before?.Code ?? '';
        const key = `${rowIndex}_${prop}`;
        items.push({ key, code, column: prop, value: newVal });
      };

      // d.models can be either:
      //  - an array of row partials (ordered top->bottom), OR
      //  - an object map: { [rowIndex]: rowPartial }
      if (Array.isArray(d.models)) {
        d.models.forEach((rowPartial: any, offset: number) => {
          const rowIndex = startRow + offset;
          // If grid provided only certain props, use them; else infer from known columns
          const props = Object.keys(rowPartial ?? {});
          if (props.length > 0) {
            props.forEach(p => pushCell(rowIndex, p, rowPartial[p]));
          } else if (Array.isArray(columnsRef.current)) {
            columnsRef.current.forEach((col: any) => {
              const p = col?.prop;
              if (!p) return;
              pushCell(rowIndex, p, (rowPartial as any)[p]);
            });
          }
        });
      } else {
        // Object map: { "12": {propA:val, ...}, "13": { ... } }
        Object.entries(d.models as Record<string, any>).forEach(([rowIndexStr, rowPartial]) => {
          const rowIndex = Number(rowIndexStr);
          Object.keys(rowPartial || {}).forEach((prop) => {
            pushCell(rowIndex, prop, (rowPartial as any)[prop]);
          });
        });
      }

      if (items.length) {
        setChangedCells(prev => {
          const map = new Map(prev.map(c => [c.key, c]));
          items.forEach(c => map.set(c.key, c));
          return [...map.values()];
        });
        // Apply range edits into React state so baseline diff triggers instantly
        setSource(prev => {
          const updated = [...prev];
          items.forEach(({ key, column, value }) => {
            const [rowIndexStr] = key.split('_');
            const rowIndex = Number(rowIndexStr);
            const row = { ...(updated[rowIndex] || {}) };
            (row as any)[column] = value;
            updated[rowIndex] = row;
          });
          return updated;
        });
        setChangedVersion(v => v + 1);
      }
      return;
    }
  };

  useEffect(() => {
    if (selectedField && selectedCode) {
      console.log("getUpdates params", {
        selectedTable,
        field: selectedField,
        code: selectedCode,
        username: profile?.email,
        selectedGroupCode,
        selectedEnvironment,
        selectedEntity
      });

      DatashaperService.getUpdates(
          selectedTable,
          selectedField,
          selectedCode
      )
          .then((resp: any) => {
            console.log("updates raw resp", resp);
            const data = resp.data;
            console.log(
                "updates after fetch",
                data,
                Array.isArray(data) ? data.length : "not array"
            );
            setUpdates(data);
          })
          .catch((err) => {
            console.error("getUpdates failed:", err);
            setUpdates([]);
          });
    }
  }, [selectedField, selectedCode]);



  // ✅ FIX: Handle autofill/drag events by reading the updated source directly
  useEffect(() => {
    const gridEl = gridRef.current?.shadowRoot?.host || gridRef.current;
    if (!gridEl) return;

    const handleAfterAutofill = async (e) => {
      console.log("Autofill event detected:", e.type, e.detail);

      // After autofill, the grid has already updated its internal data
      // We need to detect what changed by comparing with our baseline

      setTimeout(async () => {
        // Get the current grid data (after autofill)
        let gridData = source;

        if (gridRef.current) {
          const grid = gridRef.current as any;
          if (grid.getSource && typeof grid.getSource === 'function') {
            const result = grid.getSource();
            // Handle both Promise and direct value
            if (result && typeof result.then === 'function') {
              gridData = await result;
            } else if (Array.isArray(result)) {
              gridData = result;
            }
          }
        }

        // Make sure gridData is an array
        if (!Array.isArray(gridData)) {
          console.warn("Grid data is not an array:", gridData);
          return;
        }

        const newChangedCells = [...changedCells];
        const changedMap = new Map(changedCells.map(c => [c.key, c]));

        // Compare each cell with baseline to detect changes
        gridData.forEach((row: any, rowIndex: number) => {
          const rowCode = String(row?.Code ?? "");
          const baseline = baselineByCode.get(rowCode);

          columns.forEach((col: any) => {
            const prop = col.prop;
            const currentValue = row[prop];
            const baselineValue = baseline ? baseline[prop] : undefined;

            // If value differs from baseline, mark as changed
            if (currentValue !== baselineValue) {
              const key = `${rowIndex}_${prop}`;
              if (!changedMap.has(key)) {
                const changeEntry = {
                  key,
                  code: rowCode,
                  column: prop,
                  value: currentValue
                };
                newChangedCells.push(changeEntry);
                changedMap.set(key, changeEntry);
              }
            }
          });
        });

        // Update state with all detected changes
        setChangedCells(newChangedCells);
        setSource([...gridData]); // Force React to see the update
        setChangedVersion(v => v + 1);

        // Force grid repaint
        requestAnimationFrame(() => {
          (gridRef.current as any)?.updateRows?.();
          (gridRef.current as any)?.refresh?.();
        });
      }, 0);
    };

    // Listen to multiple autofill-related events
    const autofillEvents = ['afterautofill', 'afterfill', 'afterapplyrange', 'afteredit'];

    autofillEvents.forEach(eventName => {
      gridEl.addEventListener(eventName, handleAfterAutofill);
    });

    return () => {
      autofillEvents.forEach(eventName => {
        gridEl.removeEventListener(eventName, handleAfterAutofill);
      });
    };
  }, [changedCells, columns, baselineByCode, source]);

  // Add state to track current selection
  const [currentSelection, setCurrentSelection] = useState<{
    x: number;
    y: number;
    x1: number;
    y1: number;
  } | null>(null);
  const [lastValidSelection, setLastValidSelection] = useState<{
    x: number;
    y: number;
    x1: number;
    y1: number;
  } | null>(null);

  // Track selection changes - listen to multiple events
  useEffect(() => {
    const gridEl = gridRef.current?.shadowRoot?.host || gridRef.current;
    if (!gridEl) return;

    // Capture selection/range events
    const handleRangeEvent = (e) => {
      if (e.detail?.range) {
        const range = e.detail.range;
        console.log(`Selection event (${e.type}):`, range);
        setCurrentSelection(range);

        // Save multi-cell selections
        if ((Math.abs(range.x1 - range.x) > 0) || (Math.abs(range.y1 - range.y) > 0)) {
          console.log('Saving multi-cell selection from event:', range);
          setLastValidSelection(range);
        }
      }
    };

    // Listen to ALL possible range/selection events
    const selectionEvents = [
      'beforerange',
      'afterrange',
      'beforeselection',
      'afterselection',
      'beforerangeapply',
      'afterrangeapply',
      'setrange',
      'selectrange',
      'rangechanged',
      'selectionchanged',
      'beforeapplyrange'
    ];

    selectionEvents.forEach(eventName => {
      gridEl.addEventListener(eventName, handleRangeEvent);
    });

    // Track keyboard copy event to preserve selection
    const handleCopy = (e) => {
      console.log('Copy event detected, current selection:', currentSelection);
      console.log('Last valid multi-cell selection:', lastValidSelection);
    };

    document.addEventListener('copy', handleCopy);

    return () => {
      selectionEvents.forEach(eventName => {
        gridEl.removeEventListener(eventName, handleRangeEvent);
      });
      document.removeEventListener('copy', handleCopy);
    };
  }, [currentSelection, lastValidSelection]);

  useEffect(() => {
    const gridEl = gridRef.current?.shadowRoot?.host || gridRef.current;
    if (!gridEl) return;

    const handlePaste = async (e) => {
      console.log('Full paste event:', e);
      console.log('Paste event detail:', e.detail);
      console.log('Current selection state:', currentSelection);
      console.log('Last valid selection state:', lastValidSelection);

      if (!e.detail?.parsed) {
        console.warn("❌ No parsed data in paste");
        return;
      }

      setIsPasting(true); // Start paste block

      const parsedData = e.detail.parsed;
      const newUndoStack = [...undoStack];
      const changedMap = new Map(changedCells.map(c => [c.key, c]));

      // Try to use the last valid multi-cell selection first, then current selection
      let selectionRange = lastValidSelection || currentSelection;

      // If no saved selection, try to get it from the grid directly
      if (!selectionRange && gridRef.current) {
        const grid = gridRef.current as any;

        // Try multiple methods to get selection
        const possibleRange = grid.getSelectedRange?.() ||
            grid.getRange?.() ||
            grid.selection?.range ||
            grid.currentRange ||
            grid.selectedRange;

        // Handle if it's a Promise
        if (possibleRange && typeof possibleRange.then === 'function') {
          try {
            selectionRange = await possibleRange;
          } catch (err) {
            console.log('Error awaiting range:', err);
          }
        } else if (possibleRange && possibleRange.x !== undefined) {
          selectionRange = possibleRange;
        }
      }

      console.log('Using selection range:', selectionRange);

      // Determine start position and fill area
      let startRow: number;
      let startCol: number;
      let rowsToFill = 1;
      let colsToFill = 1;

      // If we have a selection range, use it
      if (selectionRange &&
          typeof selectionRange.y === 'number' &&
          typeof selectionRange.y1 === 'number' &&
          typeof selectionRange.x === 'number' &&
          typeof selectionRange.x1 === 'number') {

        // Use the selection bounds
        startRow = Math.min(selectionRange.y, selectionRange.y1);
        startCol = Math.min(selectionRange.x, selectionRange.x1);
        rowsToFill = Math.abs(selectionRange.y1 - selectionRange.y) + 1;
        colsToFill = Math.abs(selectionRange.x1 - selectionRange.x) + 1;

      } else {
        // Fall back to startCell or lastFocused
        let startCell = e.detail.startCell;
        if (!startCell || startCell.rowIndex == null || startCell.columnIndex == null) {
          startCell = lastFocusedRef.current;
        }

        if (!startCell || startCell.rowIndex == null || startCell.columnIndex == null) {
          console.warn("❌ Could not resolve paste startCell");
          setIsPasting(false);
          return;
        }

        startRow = startCell.rowIndex;
        startCol = startCell.columnIndex;
      }

      // Check if we're pasting a single value that should be repeated
      const isSingleValue = parsedData.length === 1 && parsedData[0].length === 1;
      const shouldRepeat = isSingleValue && (rowsToFill > 1 || colsToFill > 1);

      console.log("📋 Paste details:", {
        startRow,
        startCol,
        rowsToFill,
        colsToFill,
        isSingleValue,
        shouldRepeat,
        selectionRange,
        parsedData
      });

      setSource((prev) => {
        const updated = [...prev];

        if (shouldRepeat) {
          // Excel-like behavior: repeat single value across selected range
          const valueToRepeat = parsedData[0][0];

          for (let rowOffset = 0; rowOffset < rowsToFill; rowOffset++) {
            const rowIndex = startRow + rowOffset;
            if (!updated[rowIndex]) {
              console.warn(`Row ${rowIndex} doesn't exist`);
              continue;
            }
            const row = { ...(updated[rowIndex] || {}) };

            for (let colOffset = 0; colOffset < colsToFill; colOffset++) {
              const colIndex = startCol + colOffset;
              const col = columnsRef?.current[colIndex];
              if (!col) {
                console.warn("⚠️ Missing column at", colIndex);
                continue;
              }

              const prop = col?.prop;
              const key = `${rowIndex}_${prop}`;

              // Store old value for undo
              const oldValue = row[prop];
              newUndoStack.push({ rowIndex, prop, oldValue, newValue: valueToRepeat });

              // Update the cell
              row[prop] = valueToRepeat;
              const code = row.Code ?? "";

              // Update or add to changed cells
              changedMap.set(key, {
                key,
                code,
                column: prop,
                value: valueToRepeat,
              });

              console.log(`🧩 Repeating value [${key}] =`, valueToRepeat);
            }

            updated[rowIndex] = row;
          }
        } else {
          // Normal paste: paste data as provided
          parsedData.forEach((rowData, rowOffset) => {
            const rowIndex = startRow + rowOffset;
            if (!updated[rowIndex]) {
              console.warn(`Row ${rowIndex} doesn't exist`);
              return;
            }
            const row = { ...(updated[rowIndex] || {}) };

            rowData.forEach((cellValue, colOffset) => {
              const colIndex = startCol + colOffset;
              const col = columnsRef?.current[colIndex];
              if (!col) {
                console.warn("⚠️ Missing column at", colIndex);
                return;
              }

              const prop = col?.prop;
              const key = `${rowIndex}_${prop}`;

              // Store old value for undo
              const oldValue = row[prop];
              newUndoStack.push({ rowIndex, prop, oldValue, newValue: cellValue });

              // Update the cell
              row[prop] = cellValue;
              const code = row.Code ?? "";

              // Update or add to changed cells
              changedMap.set(key, {
                key,
                code,
                column: prop,
                value: cellValue,
              });

              console.log(`🧩 Setting cell [${key}] =`, cellValue);
            });

            updated[rowIndex] = row;
          });
        }

        // Update state with all changes
        setUndoStack(newUndoStack);
        setChangedCells([...changedMap.values()]);
        setChangedVersion((prev) => prev + 1);

        console.log('Updated changed cells:', [...changedMap.values()]);

        return updated;
      });

      // Reset flag after paste completes, clear the last valid selection
      setTimeout(() => {
        setIsPasting(false);
        setLastValidSelection(null); // Clear after use
        // Force grid update after paste
        (gridRef.current as any)?.refresh?.();
      }, 0);
    };

    gridEl.addEventListener("afterpasteapply", handlePaste);

    return () => {
      gridEl.removeEventListener("afterpasteapply", handlePaste);
    };
  }, [changedCells, columns, currentSelection, lastValidSelection, undoStack]);

  const handleBeforeCellFocus = (event) => {
    const { model, rowIndex, prop } = event.detail;
    if (!model) return;

    const rowCode = model.Code;

    const colIndex = columns.findIndex((c) => c.prop === prop);
    if (rowIndex != null && colIndex !== -1) {
      lastFocusedRef.current = { rowIndex, columnIndex: colIndex };
    }

    setSelectedCode(rowCode);
    setSelectedField(prop);

    // Find the matching issue
    const issue = issues.find((i) => i.code === rowCode && i.field === prop);

    setSelectedIssue(
        issue
            ? {
              code: issue.code,
              field: issue.field,
              owner: issue.issueOwner,
              mandatory: issue.mandatory ? "Yes" : "No",
              category: issue.category,
              name: issue.description,
              description: issue.longDescription,
              issueValue: issue.issueValue,
              howToSolve: issue.howToSolve,
              whoToSolve: issue.whoToSolve,
            }
            : null
    );
  };

  const handleBeforeApplyRange = (e) => {
    if (!e.detail?.range) return;
    const range = e.detail.range;
    const start = range.y;
    const end = range.y1;

    // Store the full range for paste operations
    console.log('Range applied in handleBeforeApplyRange:', range);
    setCurrentSelection(range);

    // If it's a multi-cell selection, save it as the last valid selection
    if ((Math.abs(range.x1 - range.x) > 0) || (Math.abs(range.y1 - range.y) > 0)) {
      console.log('Saving multi-cell selection:', range);
      setLastValidSelection(range);
    }

    if (typeof start !== 'number' || typeof end !== 'number') return;
    if (!source[start]) return;

    if (start === end) {
      setDeleteRows([{ code: source[start].Code.toString() }]);
      return;
    }
    const arr = [];
    for (let i = start; i <= end; i++) {
      if (source[i]) {
        arr.push({ code: source[i].Code.toString() });
      }
    }
    setDeleteRows(arr);
  };

  const handleTableChange = (v) => setSelectedTable(v);
  const handleGroupCodeChange = (v) => setSelectedGroupCode(v);
  const handleEnvironmentChange = (v) => setSelectedEnvironment(v);
  const handleEntityChange = (v) =>{
    setSelectedEntity(v);
    setSelectedTable(undefined);
    setCurrentTables([]);
  }

  const handleDowloadButtonClick = async () => {
    setLoading(true);
    const canBeCreatedAndUpdated = currentTables.find(
        (a) => a.id === selectedTable
    ).allowRowCreateDelete;
    setCanBeCreatedAndUpdated(canBeCreatedAndUpdated);
    const dataResponse = (
        await DatashaperService.getData(
            profile?.email,
            selectedTable,
            selectedGroupCode,
            selectedEnvironment,
            "0",
            "",
            "",
            "",
            "",
            "",
            "",
            ""
        )
    ).data;
    const issuesResponse = (
        await DatashaperService.getIssues(
            profile?.email,
            selectedTable,
            selectedEntity,
            selectedGroupCode!,
            selectedEnvironment!,
            "0",
            "",
            "",
            "",
            "",
            "",
            "",
            ""
        )
    ).data;
    const fieldsWithUpdatesResponse = (
        await DatashaperService.getFieldsWithUpdates(selectedTable)
    ).data;

    let editableFields = [];

    await DatashaperService.getEditableFields(selectedTable).then((resp:any) =>
    {
      editableFields = resp?.data;
    }).catch((e:any) => {
      editableFields = [];
    })

    if (!editableFields) {
      editableFields = [];
    }

    // if (issueCategory?.toLowerCase() === "duplicate") {
    //   editableFields = ["allfieldsdisabled"]
    // }

    const viewTypeId = currentTables.find(
        (a) => a.id === selectedTable
    ).viewTypeId;

    if (viewTypeId == DataShaperViewType.MappedVsEnrichment) {
      editableFields = ["EnrichedValue"]
    }

    const allIssueObj = issuesResponse.map((issue) => ({
      issueRow: issue.code,
      issueColumn: issue.field,
      issueMandatory: issue.mandatory,
      ...issue,
    }));
    setIssues(allIssueObj);

    setFieldsWithUpdates(
        fieldsWithUpdatesResponse.filter(
            (f) =>
                !allIssueObj.some(
                    (i) => i.issueRow === f.code && i.issueColumn === f.field
                )
        )
    );

    if (dataResponse && dataResponse.length > 0) {
      const arrOfColumns = Object.keys(dataResponse[0]).map((obj) => ({
        prop: obj,
        name: obj,
        readonly: !editableFields.includes(obj),
      }));

      if (canBeCreatedAndUpdated)
        dataResponse.push({ Code: "N" + counter, Test: "" });

      setColumns(arrOfColumns);
      setSource(dataResponse);

      // Build baseline from the freshly downloaded data
      const __baseline = new Map<string, any>();
      dataResponse.forEach(r => __baseline.set(String(r?.Code ?? ""), { ...r }));
      setBaselineByCode(__baseline);

      setOpen(false);
    }

    setLoading(false);
  };

  // FIX #2: Improved publish button with proper error handling
  const handlePublishButtonClick = async () => {
    setPublishLoading(true); // Use separate loading state
    try {
      const jsonData = JSON.stringify(changedCells);
      await DatashaperService.publish(
          profile?.email,
          selectedTable,
          selectedEnvironment,
          selectedGroupCode,
          jsonData
      );

      // Success case
      setChangedCells([]);
      // After publish, refresh baseline so highlights reset
      const __baselineAfterPublish = new Map<string, any>();
      (sourceRef.current || []).forEach((r: any) => __baselineAfterPublish.set(String(r?.Code ?? ""), { ...r }));
      setBaselineByCode(__baselineAfterPublish);

      // Show success message (optional)
      message.success('Published successfully!');
    } catch (error: any) {
      // Error case - show error message
      console.error('Publish failed:', error);
      message.error(error?.response?.data?.message || 'Failed to publish changes. Please try again.');
    } finally {
      // Always stop loading, whether success or error
      setPublishLoading(false);
    }
  };

  const handleRefreshButtonCLick = async () => {
    setColumns([]);
    setSource([]);
    await handleDowloadButtonClick();
    setChangedCells([]);
  };

  const handleDeleteButtonClick = async () => {
    await DatashaperService.deleteRow(
        profile?.email,
        selectedTable,
        JSON.stringify(deleteRows)
    );
    await handleRefreshButtonCLick();
  };

  const closeDownloadDrawer = () => setOpen(false);
  const showDownloadDrawer = (Download) => setOpen(true);
  const handleToggle = (checked) => setIsVisible(checked);

  const { TabPane } = Tabs;
  const cellItems = selectedIssue
      ? [
        ["Owner:", selectedIssue.owner],
        ["Mandatory:", selectedIssue.mandatory],
        ["Category:", selectedIssue.category],
        ["Name:", selectedIssue.name],
        ["Description:", selectedIssue.description],
        ["Issue Value:", selectedIssue.issueValue],
        ["How to solve:", selectedIssue.howToSolve],
        ["Who to solve:", selectedIssue.whoToSolve],
      ]
      : [["Info:", "No issue selected"]];




  /** ✅ Full renderDropdown */
  const renderDropdown = () => (
      <div className="actionsHeader">
        <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "10px",
              alignItems: "center",
            }}
        >
          <span>Show issue details:</span>
          <Switch checked={isVisible} onChange={handleToggle}></Switch>
        </div>
        <Button
            onClick={handlePublishButtonClick}
            icon={publishLoading ? <LoadingOutlined /> : <ExportOutlined /> }
            loading={publishLoading}
            disabled={publishLoading || changedCells.length === 0} // Disable when no changes
        >
          Publish
        </Button>
        <Button loading={loading} onClick={handleRefreshButtonCLick}  >
          <ReloadOutlined />
          Refresh
        </Button>
        <Button
            onClick={handleDeleteButtonClick}
            disabled={!canBeCreatedAndUpdated}
        >
          <DeleteOutlined />
          Delete
        </Button>
        <Button
            onClick={showDownloadDrawer}
            style={{ backgroundColor: "#1E1E85", color: "#fff" }}
        >
          <RightOutlined />
          Download
        </Button>
        <Drawer
            title="Download data"
            onClose={closeDownloadDrawer}
            visible={open}
            closeIcon={false}
            getContainer={false}
            headerStyle={{ padding: "10px 20px" }}
            closable={false}
            style={{ position: "absolute" }}
            extra={
              <Space>
                <Button
                    size="small"
                    onClick={closeDownloadDrawer}
                    style={{ backgroundColor: "#1E1E85", color: "#fff" }}
                >
                  <CloseOutlined />
                </Button>
              </Space>
            }
        >
          <Form layout="vertical">
            <Form.Item label="Group Code:" name="groupCode">
              <Select
                  style={{ width: "100%" }}
                  showSearch
                  allowClear
                  placeholder="Select groupcode"
                  value={selectedGroupCode}
                  onChange={handleGroupCodeChange}
                  options={groupCodes
                      .map((code) => ({ value: code, label: code }))
                      .sort((a, b) => a.label.localeCompare(b.label))}
                  filterOption={(inputValue, option) =>
                      option.label.toUpperCase().indexOf(inputValue.toUpperCase()) !==
                      -1
                  }
              />
            </Form.Item>
            <Form.Item label="Environment:" name="environment">
              <Select
                  showSearch
                  style={{ width: "100%" }}
                  allowClear
                  placeholder="Select environment"
                  value={selectedEnvironment}
                  onChange={handleEnvironmentChange}
                  options={environments
                      .map((env) => ({ value: env, label: env }))
                      .sort((a, b) => a.label.localeCompare(b.label))}
                  filterOption={(inputValue, option) =>
                      option.label.toUpperCase().indexOf(inputValue.toUpperCase()) !==
                      -1
                  }
              />
            </Form.Item>
            <Form.Item label="Entity:" name="entity">
              <Select
                  showSearch
                  style={{ width: "100%" }}
                  allowClear
                  placeholder="Select Entity"
                  value={selectedEntity}
                  onChange={handleEntityChange}
                  options={entities
                      .map((entity) => ({ value: entity, label: entity }))
                      .sort((a, b) => a.label.localeCompare(b.label))}
                  filterOption={(inputValue, option) =>
                      option.label.toUpperCase().indexOf(inputValue.toUpperCase()) !==
                      -1
                  }
              />
            </Form.Item>
            <Form.Item label="View:">
              <Select
                  showSearch
                  style={{ width: "100%" }}
                  allowClear
                  placeholder="Select view"
                  value={selectedTable}
                  onChange={handleTableChange}
                  options={currentTables
                      .map((value) => ({ value: value.id, label: value.table }))
                      .sort((a, b) => a.label.localeCompare(b.label))}
                  filterOption={(inputValue, option) =>
                      option.label.toUpperCase().indexOf(inputValue.toUpperCase()) !==
                      -1
                  }
              />
            </Form.Item>
            <Form.Item>
              <Button
                  onClick={handleDowloadButtonClick}
                  icon={loading ? <LoadingOutlined /> : <DownloadOutlined />}
                  style={{ width: "100%" }}
                  className={
                    selectedEntity &&
                    selectedEnvironment &&
                    selectedGroupCode &&
                    selectedTable
                        ? "active-btn"
                        : ""
                  }
                  disabled={
                      !selectedEntity ||
                      !selectedEnvironment ||
                      !selectedGroupCode ||
                      !selectedTable
                  }
                  loading={loading}
              >
                Download
              </Button>
            </Form.Item>
          </Form>
        </Drawer>
      </div>
  );

  return (
      <div>
        {renderDropdown()}
        <div
            style={{
              height: "85vh",
              width: "100%",
              display: "flex",
              flexDirection: "row",
            }}
        >
          <RevoGrid
              ref={gridRef}
              columns={styledColumns}
              source={source}
              onBeforecellfocus={handleBeforeCellFocus}
              onBeforeedit={handleBeforeEdit}
              onAfteredit={handleChange}
              draggable
              range
              onBeforerange={handleBeforeApplyRange}
              resize
              filter
              theme="compact"
              disableVirtualX
              rowSize={0}
              colSize={150}
              style={{ flex: 1, overflowX: "hidden" }}
          />
          <div className={`sliding-div ${isVisible ? "show" : "hide"}`}>
            <div className="dataHeader">
              <span>Issue details</span>
              <Button
                  size="small"
                  onClick={() => setIsVisible(false)}
                  style={{ backgroundColor: "#1E1E85", color: "#fff" }}
              >
                <CloseOutlined />
              </Button>
            </div>
            <Divider />
            <div className="dataContent">
              <Button>
                <DeleteOutlined />
                Remove enrichment
              </Button>
              <div className="cellTitle">
                <span>{selectedIssue?.field}</span>
                <span>{selectedIssue?.code}</span>
              </div>
              <Tabs defaultActiveKey="1">
                <TabPane tab="Issues" key="1">
                  <table style={{ borderCollapse: "collapse", width: "100%" }}>
                    <tbody>
                    {cellItems.map(([left, right], index) => (
                        <tr key={index}>
                          <td
                              style={{
                                padding: "8px",
                                width: "30%",
                                color: "#BFBFBF",
                              }}
                          >
                            {left}
                          </td>
                          <td style={{ padding: "8px" }}>{right}</td>
                        </tr>
                    ))}
                    </tbody>
                  </table>
                </TabPane>
                <TabPane tab="Updates" key="2">
                  <Updates
                      key={`${selectedIssue?.code || ''}-${selectedIssue?.field || ''}`}
                      updates={updates}
                  />
                </TabPane>
              </Tabs>
            </div>
          </div>
        </div>
      </div>
  );
};

export default DemoReport;